import styled from '@emotion/styled';
import posthog from '@leyan/lytics';
import { useMemoizedFn, useUnmount } from 'ahooks';
import { Alert, Button, message, Space, Tooltip } from 'antd';
import { useAuthorization } from 'components/Authorization';
import { isUndefined } from 'lodash-es';
import React, { Fragment, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import {
  generateStandAloneShopeeAuthUrl,
  getStandaloneAuthResult,
  LazadaStandaloneAuthResultBody,
  StandaloneAuthResult,
  StandaloneAuthResultBody,
} from 'services/api/admin';
import { AuthShopOpenMode } from 'services/api/flagr';
import { v4 as uuidv4 } from 'uuid';
import useAuthOpenMode from '../hooks/useAuthOpenMode';
import useOpenURL from '../hooks/useOpenURL';
import {
  mergeShopeePartnerScopeResult,
  type ShopeePartnerScopeResult,
} from '../utils/shopeeScopeResult';
import { SyncShopsData } from '../hooks/useShopeeAuthShop';
import { ShopTypeEnum, StorehouseType } from './ShopeeCNSCGuide';
import { ShopeeAuthShopParams, ShopeeAuthWay } from './ShopeeShopAuthWay';
import ShopeeSyncShopType from './ShopeeSyncShopType';
import { bottomButton, Footer } from './styles';
import { useTransAppProductName } from 'hooks/useTrans';
import { useOrgValue } from 'hooks/business/system/useOrg';
import { BootGuideAuthTip, GotoBootGuideButton } from 'views/BootGuide/components/GuideLink';
import { isMasterOnline, isSiteGiant } from '../../../productLine.ts';

const IllustrateWrap = styled.div`
  display: flex;
  font-size: 16px;
`;
const Highlight = styled.div`
  color: #d94b12;
`;

const BottomFooter = styled(Footer)`
  flex-direction: column;
  align-items: center;
`;
const Tips = styled(Alert)`
  padding: 4px 15px;
  margin-bottom: 16px;
`;
const ShopTable = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  margin-top: 30px;
  margin-bottom: 50px;
`;

const ShopTableHeader = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  height: 42px;
  line-height: 42px;
  background: var(--fett-bg-info-tip);
  text-align: left;
  padding: 0 16px;
`;
const ShopTableRow = Fragment;

export interface ShopeeSyncShopProps {
  authWay?: ShopeeAuthWay;
  authParams?: ShopeeAuthShopParams;
  dataSource?: Array<SyncShopsData | undefined>;
  showTips?: boolean;
  chatPartnerType?: boolean;
  erpPartnerType?: boolean;
  showRestart?: boolean;
  onRestart?: () => void;
  onFinish?: (redirect?: string) => void;
  showFinishBtn?: boolean;
  clearCache?: () => void;
  isConfigService?: boolean;
  onConfigService?: (e: any) => void;
  setSyncLoading: (value: boolean) => void;
  onAuthResult?: (type: IM_API.Admin.PartnerType, value: StandaloneAuthResult) => void;
  /** 轮询结果携带价区裁剪（authorizationScope）时回调，整页替换为裁剪提醒 */
  onScopeExcluded?: (result: LazadaStandaloneAuthResultBody) => void;
}

const ShopeeSyncShop = (props: ShopeeSyncShopProps) => {
  const {
    dataSource,
    showTips = true,
    authWay,
    chatPartnerType = true,
    erpPartnerType = true,
    showFinishBtn = true,
    showRestart = true,
    onRestart,
    onFinish,
    clearCache,
    authParams,
    onAuthResult,
    onScopeExcluded,
    setSyncLoading,
  } = props;

  const { t } = useTranslate();
  const appProductNameT = useTransAppProductName();
  const cacheRef = useRef(new Map<string, { uuid: string; timer: any }>());
  // CHAT/ERP 是两次独立授权，按授权面累积本轮成功店铺和裁剪结果，最新失败状态阻断完成，
  // 待全部必选面都成功后合并去重、一次性上抛，避免整页提醒打断另一面的授权入口
  const scopeAccumRef = useRef<{
    byPartner: Map<IM_API.Admin.PartnerType, ShopeePartnerScopeResult>;
  }>({ byPartner: new Map() });
  const [loadingCacheKey, setLoadingCacheKey] = useState<string[]>([]);
  const { payload } = useAuthorization<Oversea.Token.Payload>();

  const getAuthOpenMode = useAuthOpenMode();
  const openURL = useOpenURL();

  const orgValue = useOrgValue();
  const isExpired = orgValue?.isExpired;

  const btnDisabled = useMemo(() => {
    if (Array.isArray(dataSource)) {
      if (dataSource.length === 0) return true;

      if (chatPartnerType && erpPartnerType) {
        return !dataSource.some((item) => item?.result.chat && item?.result.erp);
      }

      if (chatPartnerType) {
        return dataSource.some((item) => !item?.result.chat);
      }

      if (erpPartnerType) {
        return dataSource.some((item) => !item?.result.erp);
      }
    }

    return true;
  }, [chatPartnerType, dataSource, erpPartnerType]);

  const illustrate = useMemo(() => {
    if (chatPartnerType && erpPartnerType) {
      const copyTips =
        authWay === ShopeeAuthWay.External ? <Highlight>{t('authShop.copyTips')}</Highlight> : null;

      return (
        <IllustrateWrap>
          <span>{t('authShop.说明')}</span>
          {btnDisabled ? (
            <div>
              <div>
                {t(
                  'authShop.添加Shopee店铺需要在卖家中心分别完成买家消息数据授权和商品订单数据授权',
                )}
              </div>
              <Highlight>{t('authShop.同一店铺必须完成两项数据授权后才能完成添加店铺')}</Highlight>
              {copyTips}
            </div>
          ) : (
            <div>
              <div>{t('authShop.添加更多店铺')}</div>
              {copyTips}
            </div>
          )}
        </IllustrateWrap>
      );
    }
    return null;
  }, [chatPartnerType, erpPartnerType, t, btnDisabled, authWay]);

  const tooltipWrap = useCallback(
    (children: React.ReactNode, key: string) => {
      if (btnDisabled) {
        const title = t('authShop.请先完成买家消息数据授权和订单商品数据授权两项授权');

        return (
          <Tooltip key={key} placement="top" title={title}>
            {children}
          </Tooltip>
        );
      }
      return children;
    },
    [btnDisabled, t],
  );

  const stopSync = useCallback((cacheKey: string) => {
    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      cached.timer && clearInterval(cached.timer);
      cacheRef.current.delete(cacheKey);
      setLoadingCacheKey((prev) => prev.filter((k) => k !== cacheKey));
    }
  }, []);

  const collectScopeResult = useMemoizedFn(
    (partner: IM_API.Admin.PartnerType | undefined, data?: StandaloneAuthResultBody) => {
      if (!partner) return;
      const acc = scopeAccumRef.current;
      const partnerResult = partner === 'CHAT' ? data?.chatResult : data?.erpResult;
      acc.byPartner.set(
        partner,
        mergeShopeePartnerScopeResult(
          acc.byPartner.get(partner),
          partnerResult,
          data?.authorizationScope,
        ),
      );

      const required: IM_API.Admin.PartnerType[] = [
        ...(chatPartnerType ? ['CHAT' as const] : []),
        ...(erpPartnerType ? ['ERP' as const] : []),
      ];
      const entries = required.map((p) => acc.byPartner.get(p));
      const allDone = required.length > 0 && entries.every((e) => !!e);
      if (!allDone) return;

      if (entries.every((e) => e!.ok)) {
        const succeededSets = entries.map((e) => e!.succeededKeys);
        const unionKeys = new Set<string>();
        succeededSets.forEach((s) => s.forEach((key) => unionKeys.add(key)));
        // 只有具备全部必选授权的店铺才算「可正常使用」（交集）；
        // 存在缺授权面的店铺时本轮不完整，不弹提醒页（避免挤掉补授权入口），留在表格里等待补齐
        const usableKeys = [...unionKeys].filter((key) => succeededSets.every((s) => s.has(key)));
        if (usableKeys.length === unionKeys.size) {
          const excluded = [...new Map(entries.flatMap((e) => [...e!.excluded])).values()];
          const scope = entries.find((e) => e?.scope)?.scope;
          if (scope && excluded.length > 0) {
            onScopeExcluded?.({
              isSuccess: true,
              errorMsg: '',
              successCount: usableKeys.length,
              authorizationScope: {
                priceZone: scope.priceZone,
                authorizedRegions: scope.authorizedRegions ?? [],
                excludedStores: excluded,
              },
            });
          }
          // 本轮完整结束（无论是否触发提醒）都重置，下一轮授权不得沿用本轮状态
          scopeAccumRef.current = { byPartner: new Map() };
        }
      }
      // 有必选面失败或存在缺面店铺时不重置：状态已由授权表格呈现，保留其余面的结果等待补齐/重试后合并
    },
  );

  const beginPartnerAuth = useMemoizedFn((partnerType: IM_API.Admin.PartnerType) => {
    const acc = scopeAccumRef.current;
    if (!acc.byPartner.get(partnerType)?.ok) return;
    const required: IM_API.Admin.PartnerType[] = [
      ...(chatPartnerType ? ['CHAT' as const] : []),
      ...(erpPartnerType ? ['ERP' as const] : []),
    ];
    const entries = required.map((p) => acc.byPartner.get(p));
    const allDone = required.length > 0 && entries.every((e) => !!e);
    const hasFailed = entries.some((e) => e && !e.ok);
    // 仅当本轮因授权面失败被搁置时，对已成功的面再次发起才视为放弃本轮、重置聚合；
    // 缺面补授权或重试都延续本轮，避免清掉已积累的成功面
    if (allDone && hasFailed) {
      scopeAccumRef.current = { byPartner: new Map() };
    }
  });

  const startSync = useMemoizedFn((uuid: string, cacheKey: string) => {
    const timer = setTimeout(async () => {
      try {
        if (!uuid) return undefined;
        const {
          data: { data },
        } = await getStandaloneAuthResult(uuid);
        let deliveredPartner: IM_API.Admin.PartnerType | undefined;
        if (chatPartnerType && data?.chatResult) {
          onAuthResult && onAuthResult('CHAT', data.chatResult);
          stopSync(cacheKey);
          deliveredPartner = 'CHAT';
        } else if (erpPartnerType && data?.erpResult) {
          onAuthResult && onAuthResult('ERP', data.erpResult);
          stopSync(cacheKey);
          deliveredPartner = 'ERP';
        } else {
          const cached = cacheRef.current.get(cacheKey);
          if (cached && cached.timer) {
            clearTimeout(cached.timer);
            cacheRef.current.set(cacheKey, {
              ...cached,
              timer: startSync(uuid, cacheKey),
            });
          }
        }
        collectScopeResult(deliveredPartner, data);
        return data;
      } catch (err) {
        message.error(t('authShop.获取Shopee添加店铺信息失败'));
        stopSync(cacheKey);
        return undefined;
      }
    }, 2000);

    return timer;
  });

  const getShopeeAuthURL = useCallback(
    async (uuid: string, partnerType: IM_API.Admin.PartnerType, openMode?: AuthShopOpenMode) => {
      try {
        if (partnerType && authParams && !isUndefined(authWay)) {
          let storehouseType: StorehouseType | undefined;
          if (authParams.shopType === ShopTypeEnum.Cross3PF) {
            storehouseType = StorehouseType.External;
          }
          if (authParams.shopType === ShopTypeEnum.CrossOfficial) {
            storehouseType = StorehouseType.Official;
          }

          const { data } = await generateStandAloneShopeeAuthUrl(
            partnerType,
            authParams.storeType,
            authParams.region,
            authParams.departmentIds || [],
            uuid,
            authWay,
            openMode,
            storehouseType,
          );
          if (data?.code === 200 && data?.data?.url) {
            return data?.data?.url;
          }
        }
        throw new Error('请求 Shopee 授权链接失败');
      } catch (err) {
        message.error(t('authShop.获取授权链接失败'));
        return null;
      }
    },
    [authParams, authWay, t],
  );

  const handleOpen = useCallback(
    async (key: string, partnerType: IM_API.Admin.PartnerType) => {
      const cached = cacheRef.current.get(key);
      if (cached) {
        stopSync(key);
      }

      const openMode = await getAuthOpenMode('shopee');

      const uuid = uuidv4();
      const url = await getShopeeAuthURL(uuid, partnerType, openMode);
      if (url) {
        beginPartnerAuth(partnerType);
        cacheRef.current.set(key, {
          uuid,
          timer: startSync(uuid, key),
        });
        setLoadingCacheKey([...loadingCacheKey, key]);

        openURL(url, openMode);

        posthog.capture('$authShops', {
          result: JSON.stringify({
            platform: 'shopee',
            orgId: payload?.orgId,
            phoneNumber: payload?.phoneNumber,
            component: 'ShopeeSyncShopOpenURL',
            authWay,
            authParams,
            partnerType,
            openMode,
            uuid,
          }),
        });
      }
    },
    [
      authParams,
      authWay,
      beginPartnerAuth,
      getAuthOpenMode,
      getShopeeAuthURL,
      loadingCacheKey,
      openURL,
      payload?.orgId,
      payload?.phoneNumber,
      startSync,
      stopSync,
    ],
  );

  const handleCopy = useCallback(
    async (key: string, partnerType: IM_API.Admin.PartnerType, noPrefix = false) => {
      const cached = cacheRef.current.get(key);
      if (cached) {
        stopSync(key);
      }

      const uuid = uuidv4();
      const url = await getShopeeAuthURL(uuid, partnerType);
      if (url) {
        beginPartnerAuth(partnerType);
        cacheRef.current.set(key, {
          uuid,
          timer: startSync(uuid, key),
        });

        let template = '';
        if (partnerType === 'CHAT') {
          template = noPrefix ? url : appProductNameT('authShop.copyLinkTemplate1') + url;
        }
        if (partnerType === 'ERP') {
          template = noPrefix ? url : appProductNameT('authShop.copyLinkTemplate2') + url;
        }

        if (!template) {
          message.success(t('authShop.复制链接失败'));
          return;
        }
        setLoadingCacheKey([...loadingCacheKey, key]);
        await navigator.clipboard.writeText(template);
        message.success(t('authShop.复制成功'));
        posthog.capture('$authShops', {
          result: JSON.stringify({
            platform: 'shopee',
            orgId: payload?.orgId,
            phoneNumber: payload?.phoneNumber,
            component: 'ShopeeSyncShopCopyURL',
            authWay,
            authParams,
            partnerType,
          }),
        });
      }
    },
    [
      authParams,
      authWay,
      beginPartnerAuth,
      getShopeeAuthURL,
      loadingCacheKey,
      payload?.orgId,
      payload?.phoneNumber,
      startSync,
      stopSync,
      t,
      appProductNameT,
    ],
  );

  const getShopTableRender = useCallback(
    (tableIndex: number, shopNames?: string[], result?: SyncShopsData['result']) => {
      let shopNameHeader = <ShopTableHeader>{t('authShop.店铺信息')}</ShopTableHeader>;
      if (shopNames && shopNames.length > 0) {
        let text = shopNames[0];
        if (shopNames.length > 1) {
          text = `${shopNames[0]}等${shopNames.length}个店铺`;
        }
        shopNameHeader = <ShopTableHeader>{text}</ShopTableHeader>;
      }

      let count = 0;
      if (result?.chat && result?.erp) {
        count = 2;
      } else if (result?.chat || result?.erp) {
        count = 1;
      }

      const isChatLoading = loadingCacheKey.some(
        (k) => k === `chat_open_${tableIndex}` || k === `chat_copy_${tableIndex}`,
      );
      const isErpLoading = loadingCacheKey.some(
        (k) => k === `erp_open_${tableIndex}` || k === `erp_copy_${tableIndex}`,
      );

      return (
        <ShopTable>
          <ShopTableRow>
            {shopNameHeader}
            <ShopTableHeader>
              {t('authShop.授权状态', { count, total: chatPartnerType && erpPartnerType ? 2 : 1 })}
            </ShopTableHeader>
          </ShopTableRow>
          {chatPartnerType ? (
            <ShopTableRow>
              <ShopeeSyncShopType
                chatPartnerType={chatPartnerType}
                authWay={authWay}
                result={result?.chat}
                errorMsg={result?.chatErrorMsg}
                onAuthResult={onAuthResult}
                setSyncLoading={setSyncLoading}
                loading={isChatLoading}
                onOpen={() => handleOpen(`chat_open_${tableIndex}`, 'CHAT')}
                onCopy={(noPrefix) => handleCopy(`chat_copy_${tableIndex}`, 'CHAT', noPrefix)}
              />
            </ShopTableRow>
          ) : null}
          {erpPartnerType ? (
            <ShopTableRow>
              <ShopeeSyncShopType
                erpPartnerType={erpPartnerType}
                authWay={authWay}
                result={result?.erp}
                errorMsg={result?.erpErrorMsg}
                onAuthResult={onAuthResult}
                setSyncLoading={setSyncLoading}
                loading={isErpLoading}
                onOpen={() => handleOpen(`erp_open_${tableIndex}`, 'ERP')}
                onCopy={(noPrefix) => handleCopy(`erp_copy_${tableIndex}`, 'ERP', noPrefix)}
              />
            </ShopTableRow>
          ) : null}
        </ShopTable>
      );
    },
    [
      authWay,
      chatPartnerType,
      erpPartnerType,
      handleCopy,
      handleOpen,
      loadingCacheKey,
      onAuthResult,
      setSyncLoading,
      t,
    ],
  );

  const primaryButton = useMemo(() => {
    if (showFinishBtn) {
      return tooltipWrap(
        isExpired ? (
          isMasterOnline || isSiteGiant ? null : (
            <Button
              className={bottomButton}
              type="primary"
              size="large"
              disabled={btnDisabled}
              onClick={() => onFinish?.('/management/service/market')}
            >
              {t('shop_auth_result_goto_order_package')}
            </Button>
          )
        ) : (
          <GotoBootGuideButton
            size="large"
            disabled={btnDisabled}
            onClick={() => onFinish?.()}
            fallback={
              <Button
                className={bottomButton}
                type="primary"
                size="large"
                disabled={btnDisabled}
                onClick={() => onFinish?.('/conversation')}
              >
                {t('authShop.立即开始接待')}
              </Button>
            }
          />
        ),
        'finish',
      );
    }

    return null;
  }, [btnDisabled, onFinish, showFinishBtn, t, tooltipWrap, isExpired]);

  const renderTable = useMemo(() => {
    if (Array.isArray(dataSource) && dataSource.length > 0) {
      return dataSource.map((item, index) =>
        getShopTableRender(index, item?.shopNames, item?.result),
      );
    }
    return getShopTableRender(0, []);
  }, [dataSource, getShopTableRender]);

  useUnmount(() => {
    for (const [key] of cacheRef.current) {
      stopSync(key);
    }

    cacheRef.current.clear();
    clearCache && clearCache();
  });

  return (
    <>
      {illustrate}
      {renderTable}
      <BottomFooter>
        {showTips && !btnDisabled ? (
          <Tips
            type="warning"
            message={t('authShop.首次添加店铺完成后需等待15分钟，系统自动同步消息内容及订单信息')}
            showIcon
          />
        ) : null}
        {!btnDisabled && <BootGuideAuthTip className="mb-[26px]" />}
        <Space>
          {primaryButton}
          {showRestart
            ? tooltipWrap(
                <Button
                  className={bottomButton}
                  type={showFinishBtn ? 'default' : 'primary'}
                  size="large"
                  disabled={btnDisabled}
                  onClick={onRestart}
                >
                  {t('authShop.再次添加新的店铺')}
                </Button>,
                'restart',
              )
            : null}
        </Space>
      </BottomFooter>
    </>
  );
};

export default ShopeeSyncShop;
